import React from 'react';

export type TrackType = 'uploaded' | 'youtube' | 'drive';

export interface TrackTypeInfo {
  type: TrackType;
  label: string;
}

/**
 * Determines the track type based on file metadata
 */
export function getTrackType(file: {
  driveFileId?: string | null;
  driveFileUrl?: string | null;
  youtubeUrl?: string | null;
}): TrackTypeInfo {
  // Check for Drive files - both driveFileId and driveFileUrl should be checked
  const hasDriveFileId = file.driveFileId && file.driveFileId.trim() !== '';
  const hasDriveFileUrl = file.driveFileUrl && file.driveFileUrl.trim() !== '';
  
  if (hasDriveFileId || hasDriveFileUrl) {
    return { type: 'drive', label: 'FROM GOOGLE DRIVE' };
  }
  
  // Check for YouTube files
  const hasYoutubeUrl = file.youtubeUrl && file.youtubeUrl.trim() !== '';
  if (hasYoutubeUrl) {
    return { type: 'youtube', label: 'FROM YOUTUBE' };
  }
  
  return { type: 'uploaded', label: 'UPLOADED' };
}

/**
 * Renders the appropriate icon for a track type with proper styling matching metadata previews
 * @param trackType - The type of track (uploaded, youtube, drive)
 * @param className - Optional CSS class for the icon container
 * @param size - Size of the icon in pixels (default: 20)
 * @param iconSize - Size of the actual icon inside the container (default: same as size)
 */
export function renderTrackTypeIcon(
  trackType: TrackType,
  className?: string,
  size: number = 20,
  iconSize?: number
): React.ReactNode {
  const actualIconSize = iconSize || size;
  
  // Colors matching metadata previews
  const styles: Record<TrackType, { bg: string; color: string }> = {
    uploaded: { bg: '#E6F7F8', color: '#5BB0B5' }, // teal background, teal icon
    youtube: { bg: '#FFF0F0', color: '#E62117' }, // light red background, red icon
    drive: { bg: '#EFF6FF', color: 'transparent' } // light blue background, PNG icon
  };
  
  const style = styles[trackType];
  
  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: style.bg,
    flexShrink: 0,
    overflow: 'hidden'
  };
  
  // Icon size is 70% of container size for better visual balance
  const iconScale = 0.7;
  
  switch (trackType) {
    case 'drive':
      return (
        <div style={containerStyle} className={className}>
          <img
            src="https://ssl.gstatic.com/docs/doclist/images/drive_2022q3_32dp.png"
            alt="Google Drive"
            style={{ width: size * iconScale, height: size * iconScale, objectFit: 'contain' }}
          />
        </div>
      );
    case 'youtube':
      return (
        <div style={containerStyle} className={className}>
          <svg viewBox="0 0 24 24" width={size * iconScale} height={size * iconScale} style={{ color: style.color }}>
            <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" fill="currentColor" />
          </svg>
        </div>
      );
    case 'uploaded':
    default:
      return (
        <div style={containerStyle} className={className}>
          <svg viewBox="0 0 24 24" width={size * iconScale} height={size * iconScale} style={{ color: style.color }}>
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" fill="currentColor" />
          </svg>
        </div>
      );
  }
}

